<template>
  <div class="cal">
    <div class="grid-header">
      <div v-for="d in dayLabels" :key="d" class="dow">{{ d }}</div>
    </div>
    <div class="grid-body">
      <CalendarCell
        v-for="cell in cells"
        :key="cell.date"
        :date="cell.date"
        :is-outside-month="cell.outside"
        :is-selected="selectedSet.has(cell.date)"
        :slots="cell.slots"
        :override-markers="cell.overrideMarkers"
        @select="onCellSelect"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import CalendarCell from "./CalendarCell.vue";

export default {
  name: "CalendarGrid",
  components: { CalendarCell },
  props: {
    year: { type: Number, required: true },
    month: { type: Number, required: true }, // 1..12
    days: { type: Array, default: () => [] }, // [{date, slots, overrideMarkers}]
    selectedDates: { type: Array, default: () => [] },
  },
  data() {
    return {
      dayLabels: ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"],
      anchor: null, // last single/additive click, for shift-range
    };
  },
  computed: {
    selectedSet() {
      return new Set(this.selectedDates);
    },
    cells() {
      // moment is mutable: clone before any add/subtract.
      const monthStart = moment(
        `${this.year}-${String(this.month).padStart(2, "0")}-01`
      );
      const monthEnd = monthStart.clone().endOf("month");
      const startDow = monthStart.day(); // Sunday=0
      const endDow = monthEnd.day();
      const start = monthStart.clone().subtract(startDow, "day");
      const end = monthEnd.clone().add(6 - endDow, "day");
      const out = [];
      const d = start.clone();
      const dayMap = new Map(this.days.map((dd) => [dd.date, dd]));
      while (d.isBefore(end) || d.isSame(end, "day")) {
        const ds = d.format("YYYY-MM-DD");
        const found = dayMap.get(ds);
        out.push({
          date: ds,
          outside: d.month() + 1 !== this.month,
          slots: found ? found.slots : [],
          overrideMarkers: found ? found.overrideMarkers : [],
        });
        d.add(1, "day");
      }
      return out;
    },
    // In-month dates in calendar order, for range selection.
    inMonthDates() {
      return this.cells.filter((c) => !c.outside).map((c) => c.date);
    },
  },
  methods: {
    onCellSelect({ date, additive, range }) {
      // Ignore clicks on adjacent-month padding cells.
      if (!this.inMonthDates.includes(date)) return;

      if (range && this.anchor) {
        const a = this.inMonthDates.indexOf(this.anchor);
        const b = this.inMonthDates.indexOf(date);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          this.emit(this.inMonthDates.slice(lo, hi + 1));
          return;
        }
      }

      if (additive) {
        const set = new Set(this.selectedDates);
        if (set.has(date)) set.delete(date);
        else set.add(date);
        this.anchor = date;
        this.emit([...set]);
        return;
      }

      // Plain click: select just this date.
      this.anchor = date;
      this.emit([date]);
    },
    emit(dates) {
      // Keep chronological order for stable headers/labels.
      this.$emit("update:selectedDates", [...dates].sort());
    },
  },
};
</script>

<style scoped>
.cal {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.grid-header,
.grid-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}
.grid-header {
  margin-bottom: 5px;
}
.grid-body {
  grid-auto-rows: minmax(56px, 1fr);
  flex: 1;
  min-height: 0;
}
.dow {
  padding-bottom: 2px;
  text-align: center;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--faint);
}
</style>
